import React, { useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { isEmpty } from "lodash"
import { SandboxContext } from "@/layout/Nav/SandboxSwitcher"
import { Loader } from "@/UI"
import { useRecoilValue } from "recoil"
import { isPayingUserSelector, userAtom } from "@/atoms/userAtom"
import { faChevronDown, faDownload, faPlus, faUpload } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import EmptySettingBox from "../shared/EmptySettingBox.container"
import SettingSection from "../shared/SettingSection"
import { CloudUploadOutlined } from "@ant-design/icons"
import { useSandboxRouter } from "@/hooks/useSandboxRouter"
import { PROFILE_PAGE_PATH } from "@/constants/routes"
import PreventWithModal from "@/UI/PreventWithModal"
import { Client, CreateClientPayload } from "@/types/clients.types"
import { useClients } from "@/hooks/useClients"
import ClientsAddEditModal from "./components/ClientsModal"
import ClientsTable from "./components/ClientsTable"
import SettingContentWrapper from "../shared/SettingContentWrapper"
import { Dropdown, Menu } from "antd"
import ClientsUpload from "./components/ClientsUpload"

interface ClientsPageProps {}

const ClientsPage: React.FC<ClientsPageProps> = () => {
  const { t } = useTranslation()
  const { enrichedClients, createClient, fetchClients, updateClient, removeClient, isLoading } =
    useClients()
  const isPayingUser = useRecoilValue(isPayingUserSelector)
  const { sandboxRouter } = useSandboxRouter()
  const sandbox = useContext(SandboxContext)
  const user = useRecoilValue(userAtom)
  const [editingClient, setEditingClient] = useState<Client | null>(null)
  const [uploadOpen, setUploadOpen] = React.useState<boolean>(false)

  const handleCreateClient: (client: CreateClientPayload) => Promise<void> = async (client) => {
    try {
      await createClient(client)
    } catch (error) {
      console.log("error creating keys", error)
    }
  }

  const handleUpdateClient: (client: Client) => Promise<void> = async (client) => {
    try {
      await updateClient(client)
      setEditingClient(null)
    } catch (error) {
      console.log("error updating keys", error)
    }
  }

  const handleDeleteClient: (id: string) => Promise<void> = async (id) => {
    try {
      await removeClient(id)
    } catch (error) {
      console.log("error deleting keys", error)
      throw error
    }
  }

  const handleOpenEditModal = (client: Client) => {
    setEditingClient(client)
  }

  useEffect(() => {
    fetchClients()
  }, [sandbox.enabled, user.org.id])

  const menu = (
    <Menu>
      <Menu.Item
        key="1"
        disabled={!isPayingUser}
        onClick={() => {
          window.open("https://static.kosmo.delivery/docs/prod/importClientTemplate.csv")
        }}
      >
        <FontAwesomeIcon className="mr-2" icon={faDownload} />
        {t("common.downloadTemplate")}
      </Menu.Item>
      <Menu.Item key="2" onClick={() => setUploadOpen(true)} disabled={!isPayingUser}>
        <FontAwesomeIcon className="mr-2" icon={faUpload} />
        {t("profile.Clients.importClients")}
      </Menu.Item>
    </Menu>
  )

  const createClientButton = (
    <Dropdown.Button
      overlay={menu}
      type="primary"
      trigger={["click"]}
      style={{
        width: "auto",
      }}
      icon={<FontAwesomeIcon icon={faChevronDown} color="#FFF" />}
    >
      <div
        className="flex items-center gap-x-2"
        style={{
          fontSize: "14px",
        }}
      >
        <div className="flex items-center gap-x-2">
          <FontAwesomeIcon icon={faPlus} color="#FFF" />
          <p>{t("profile.Clients.createClient")}</p>
        </div>
      </div>
    </Dropdown.Button>
  )

  const addEditModal = (
    <ClientsAddEditModal
      title={editingClient ? t("profile.Clients.editClient") : t("profile.Clients.createClient")}
      button={createClientButton}
      handleCreateClient={handleCreateClient}
      handleUpdateClient={handleUpdateClient}
      fetchClients={fetchClients}
      client={editingClient}
      isOpen={!!editingClient}
      onClose={() => setEditingClient(null)}
    />
  )

  const preventedModal = (
    <PreventWithModal
      enable={!isPayingUser}
      label={t("onboarding.freePlanBannerDescription")}
      cta={{
        label: t("profile.planCard.startNow"),
        onClick: () => sandboxRouter.push(`${PROFILE_PAGE_PATH}/?p=plans`),
      }}
    >
      {createClientButton}
    </PreventWithModal>
  )

  return (
    <SettingSection
      title={t("profile.Clients.title")}
      actionButton={
        !isPayingUser ? (
          preventedModal
        ) : (
          <>
            <ClientsUpload
              open={uploadOpen}
              onClose={() => {
                setUploadOpen(false)
              }}
            />
            {addEditModal}
          </>
        )
      }
    >
      {isEmpty(enrichedClients) ? (
        <>
          {isLoading ? (
            <Loader fontSize={46} />
          ) : (
            <SettingContentWrapper>
              <EmptySettingBox
                id="empty-customer-portal"
                icon={
                  <CloudUploadOutlined
                    style={{
                      fontSize: 100,
                      color: "#C4C4C4",
                    }}
                  />
                }
                description={t("profile.Clients.startCreatingPortal")}
                button={isPayingUser ? addEditModal : preventedModal}
              />
            </SettingContentWrapper>
          )}
        </>
      ) : (
        <div className="flex flex-col w-full gap-y-4">
          <ClientsTable
            clients={[...enrichedClients]}
            openEditClient={handleOpenEditModal}
            deleteClients={handleDeleteClient}
          />
        </div>
      )}
    </SettingSection>
  )
}

export default ClientsPage
